/**
 * Node Parameter Schemas
 *
 * Defines the required and optional parameters for each node type.
 * Used for validation during JSON workflow creation.
 */

import { WorkflowNodeType } from './workflow-json.types';

// =============================================================================
// Parameter Schema Types
// =============================================================================

/**
 * Type of parameter value
 */
export type ParameterValueType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'array'
  | 'object'
  | 'enum';

/**
 * Definition of a single parameter
 */
export interface ParameterDefinition {
  /** Parameter name/key */
  name: string;
  /** Type of the parameter value */
  type: ParameterValueType;
  /** Whether this parameter is required */
  required: boolean;
  /** Human-readable description */
  description?: string;
  /** Default value if not provided */
  defaultValue?: unknown;
  /** Allowed values for enum types */
  enumValues?: string[];
  /** Nested parameter definitions for object types */
  properties?: ParameterDefinition[];
  /** Item type for array types */
  itemType?: ParameterValueType;
  /** Nested schema for array items of object type */
  itemSchema?: ParameterDefinition[];
  /**
   * entityName values that satisfy this requirement when found in the node's
   * `dynamicFields` array (case-insensitive), e.g. a "Domain" field mapped from
   * an upstream node's output instead of a flat parameter value.
   */
  dynamicFieldEntityNames?: string[];
}

/**
 * Schema definition for a node type
 */
export interface NodeParameterSchema {
  nodeType: WorkflowNodeType;
  description: string;
  category: string;
  parameters: ParameterDefinition[];
}

// =============================================================================
// Parameter Schemas for Each Node Type
// =============================================================================

/**
 * All node parameter schemas indexed by node type
 */
export const NODE_PARAMETER_SCHEMAS: Record<string, NodeParameterSchema> = {
  // =========================================================================
  // Trigger Nodes
  // =========================================================================
  manualTrigger: {
    nodeType: 'manualTrigger',
    description: 'Manually trigger the workflow',
    category: 'trigger',
    parameters: [], // No required parameters
  },

  scheduleTrigger: {
    nodeType: 'scheduleTrigger',
    description: 'Trigger workflow on a schedule',
    category: 'trigger',
    parameters: [
      {
        name: 'scheduleType',
        type: 'enum',
        required: true,
        description: 'Type of schedule',
        enumValues: ['interval', 'cron', 'once'],
      },
      {
        name: 'intervalValue',
        type: 'number',
        required: false,
        description: 'Interval value (for interval type)',
      },
      {
        name: 'intervalUnit',
        type: 'enum',
        required: false,
        description: 'Interval unit',
        enumValues: ['minutes', 'hours', 'days', 'weeks'],
      },
      {
        name: 'cronExpression',
        type: 'string',
        required: false,
        description: 'Cron expression (for cron type)',
      },
      {
        name: 'executeAt',
        type: 'number',
        required: false,
        description: 'Unix timestamp (for once type)',
      },
      {
        name: 'timezone',
        type: 'string',
        required: false,
        description: 'Timezone for scheduling',
      },
    ],
  },

  webhookTrigger: {
    nodeType: 'webhookTrigger',
    description: 'Trigger workflow via webhook',
    category: 'trigger',
    parameters: [
      {
        name: 'path',
        type: 'string',
        required: false,
        description: 'Custom webhook path',
      },
      {
        name: 'method',
        type: 'enum',
        required: false,
        description: 'HTTP method',
        enumValues: ['POST', 'GET', 'PUT', 'DELETE'],
        defaultValue: 'POST',
      },
    ],
  },

  gmailTrigger: {
    nodeType: 'gmailTrigger',
    description: 'Trigger on Gmail events',
    category: 'trigger',
    parameters: [
      {
        name: 'triggerType',
        type: 'enum',
        required: false,
        description: 'Type of Gmail event',
        enumValues: ['newEmail', 'newThread', 'labelAdded', 'emailModified'],
      },
      {
        name: 'pollingInterval',
        type: 'number',
        required: false,
        description: 'Polling interval in minutes',
      },
    ],
  },

  googleSheetsTrigger: {
    nodeType: 'googleSheetsTrigger',
    description: 'Trigger on Google Sheets events',
    category: 'trigger',
    parameters: [
      {
        name: 'triggerType',
        type: 'enum',
        required: false,
        description: 'Type of Sheets event',
        enumValues: ['rowAdded', 'rowUpdated', 'cellChanged', 'sheetModified'],
      },
      {
        name: 'spreadsheetId',
        type: 'string',
        required: false,
        description: 'Google Spreadsheet ID',
      },
      {
        name: 'sheetName',
        type: 'string',
        required: false,
        description: 'Sheet name within the spreadsheet',
      },
    ],
  },

  httpTrigger: {
    nodeType: 'httpTrigger',
    description: 'Trigger workflow via HTTP request',
    category: 'trigger',
    parameters: [
      {
        name: 'path',
        type: 'string',
        required: false,
        description: 'Custom trigger path',
      },
    ],
  },

  slackTrigger: {
    nodeType: 'slackTrigger',
    description: 'Trigger on Slack events',
    category: 'trigger',
    parameters: [
      {
        name: 'triggerType',
        type: 'string',
        required: false,
        description: 'Type of Slack event',
      },
    ],
  },

  // Platform trigger nodes
  accountCreated: {
    nodeType: 'accountCreated',
    description: 'Trigger when an account is created',
    category: 'trigger',
    parameters: [
      {
        name: 'templateType',
        type: 'string',
        required: false,
        description: 'Template type for the trigger',
      },
    ],
  },

  accountCreatedNode: {
    nodeType: 'accountCreatedNode',
    description: 'Trigger when an account is created',
    category: 'trigger',
    parameters: [
      {
        name: 'templateType',
        type: 'string',
        required: false,
        description: 'Template type for the trigger',
      },
    ],
  },

  accountUpdated: {
    nodeType: 'accountUpdated',
    description: 'Trigger when an account is updated',
    category: 'trigger',
    parameters: [
      {
        name: 'templateType',
        type: 'string',
        required: false,
        description: 'Template type for the trigger',
      },
    ],
  },

  accountUpdatedNode: {
    nodeType: 'accountUpdatedNode',
    description: 'Trigger when an account is updated',
    category: 'trigger',
    parameters: [
      {
        name: 'templateType',
        type: 'string',
        required: false,
        description: 'Template type for the trigger',
      },
    ],
  },

  contactCreated: {
    nodeType: 'contactCreated',
    description: 'Trigger when a contact is created',
    category: 'trigger',
    parameters: [
      {
        name: 'templateType',
        type: 'string',
        required: false,
        description: 'Template type for the trigger',
      },
    ],
  },

  contactCreatedNode: {
    nodeType: 'contactCreatedNode',
    description: 'Trigger when a contact is created',
    category: 'trigger',
    parameters: [
      {
        name: 'templateType',
        type: 'string',
        required: false,
        description: 'Template type for the trigger',
      },
    ],
  },

  contactUpdated: {
    nodeType: 'contactUpdated',
    description: 'Trigger when a contact is updated',
    category: 'trigger',
    parameters: [
      {
        name: 'templateType',
        type: 'string',
        required: false,
        description: 'Template type for the trigger',
      },
    ],
  },

  contactUpdatedNode: {
    nodeType: 'contactUpdatedNode',
    description: 'Trigger when a contact is updated',
    category: 'trigger',
    parameters: [
      {
        name: 'templateType',
        type: 'string',
        required: false,
        description: 'Template type for the trigger',
      },
    ],
  },

  // =========================================================================
  // Logic Nodes
  // =========================================================================
  if: {
    nodeType: 'if',
    description: 'Conditional branching based on conditions',
    category: 'logic',
    parameters: [
      {
        name: 'conditions',
        type: 'array',
        required: true,
        description: 'Array of conditions to evaluate',
        itemType: 'object',
        itemSchema: [
          {
            name: 'field',
            type: 'string',
            required: true,
            description: 'Field to evaluate',
          },
          {
            name: 'operator',
            type: 'enum',
            required: true,
            description: 'Comparison operator',
            enumValues: ['equals', 'notEquals', 'contains', 'notContains', 'exists', 'notExists', 'greaterThan', 'lessThan'],
          },
          {
            name: 'value',
            type: 'string',
            required: false,
            description: 'Value to compare against',
          },
        ],
      },
    ],
  },

  condition: {
    nodeType: 'condition',
    description: 'Simple conditional node',
    category: 'logic',
    parameters: [
      {
        name: 'field',
        type: 'string',
        required: true,
        description: 'Field to evaluate',
      },
      {
        name: 'operator',
        type: 'enum',
        required: true,
        description: 'Comparison operator',
        enumValues: ['exist', 'equal', 'notEqual', 'contains', 'notContains'],
      },
      {
        name: 'value',
        type: 'string',
        required: false,
        description: 'Value to compare against',
      },
    ],
  },

  conditionNode: {
    nodeType: 'conditionNode',
    description: 'Simple conditional node',
    category: 'logic',
    parameters: [
      {
        name: 'field',
        type: 'string',
        required: true,
        description: 'Field to evaluate',
      },
      {
        name: 'operator',
        type: 'enum',
        required: true,
        description: 'Comparison operator',
        enumValues: ['exist', 'equal', 'notEqual', 'contains', 'notContains'],
      },
      {
        name: 'value',
        type: 'string',
        required: false,
        description: 'Value to compare against',
      },
    ],
  },

  switch: {
    nodeType: 'switch',
    description: 'Multi-way branching based on field value',
    category: 'logic',
    parameters: [
      {
        name: 'field',
        type: 'string',
        required: true,
        description: 'Field to evaluate',
      },
      {
        name: 'cases',
        type: 'array',
        required: false,
        description: 'Array of cases',
        itemType: 'object',
        itemSchema: [
          {
            name: 'value',
            type: 'string',
            required: true,
            description: 'Value to match',
          },
          {
            name: 'operator',
            type: 'enum',
            required: false,
            description: 'Match operator',
            enumValues: ['equals', 'contains', 'startsWith', 'endsWith', 'regex'],
            defaultValue: 'equals',
          },
          {
            name: 'output',
            type: 'string',
            required: false,
            description: 'Output handle name',
          },
        ],
      },
    ],
  },

  merge: {
    nodeType: 'merge',
    description: 'Merge multiple branches',
    category: 'logic',
    parameters: [
      {
        name: 'mode',
        type: 'enum',
        required: false,
        description: 'Merge mode',
        enumValues: ['append', 'merge', 'chooseBranch'],
        defaultValue: 'append',
      },
    ],
  },

  mergeNode: {
    nodeType: 'mergeNode',
    description: 'Merge multiple branches',
    category: 'logic',
    parameters: [
      {
        name: 'mode',
        type: 'enum',
        required: false,
        description: 'Merge mode',
        enumValues: ['append', 'merge', 'chooseBranch'],
        defaultValue: 'append',
      },
    ],
  },

  set: {
    nodeType: 'set',
    description: 'Set field values',
    category: 'logic',
    parameters: [
      {
        name: 'values',
        type: 'array',
        required: false,
        description: 'Values to set',
        itemType: 'object',
        itemSchema: [
          {
            name: 'name',
            type: 'string',
            required: true,
            description: 'Field name',
          },
          {
            name: 'value',
            type: 'string',
            required: true,
            description: 'Field value',
          },
        ],
      },
    ],
  },

  mapFields: {
    nodeType: 'mapFields',
    description: 'Map fields from one format to another',
    category: 'data',
    parameters: [
      {
        name: 'mappings',
        type: 'array',
        required: false,
        description: 'Field mappings',
        itemType: 'object',
        itemSchema: [
          {
            name: 'sourceField',
            type: 'string',
            required: true,
            description: 'Source field name',
          },
          {
            name: 'targetField',
            type: 'string',
            required: true,
            description: 'Target field name',
          },
          {
            name: 'transform',
            type: 'enum',
            required: false,
            description: 'Transformation to apply',
            enumValues: ['none', 'uppercase', 'lowercase', 'trim', 'format'],
          },
        ],
      },
    ],
  },

  code: {
    nodeType: 'code',
    description: 'Execute custom JavaScript on workflow data',
    category: 'logic',
    parameters: [
      {
        name: 'code',
        type: 'string',
        required: true,
        description: 'JavaScript code to execute',
      },
    ],
  },

  codeNode: {
    nodeType: 'codeNode',
    description: 'Execute custom JavaScript on workflow data',
    category: 'logic',
    parameters: [
      {
        name: 'code',
        type: 'string',
        required: true,
        description: 'JavaScript code to execute',
      },
    ],
  },

  defineVariablesNode: {
    nodeType: 'defineVariablesNode',
    description: 'Define named variables for use in downstream nodes',
    category: 'logic',
    parameters: [
      {
        name: 'variables',
        type: 'array',
        required: true,
        description: 'Named variables to define',
      },
    ],
  },

  wait: {
    nodeType: 'wait',
    description: 'Pause workflow execution for a defined number of minutes',
    category: 'logic',
    parameters: [
      {
        name: 'waitMinutes',
        type: 'number',
        required: true,
        description: 'Number of minutes to wait',
      },
    ],
  },

  // =========================================================================
  // Communication Nodes
  // =========================================================================
  httpRequest: {
    nodeType: 'httpRequest',
    description: 'Make HTTP request',
    category: 'communication',
    parameters: [
      {
        name: 'url',
        type: 'string',
        required: true,
        description: 'URL to request',
      },
      {
        name: 'method',
        type: 'enum',
        required: false,
        description: 'HTTP method',
        enumValues: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'],
        defaultValue: 'GET',
      },
      {
        name: 'headers',
        type: 'array',
        required: false,
        description: 'Request headers',
      },
      {
        name: 'bodyContent',
        type: 'string',
        required: false,
        description: 'Request body',
      },
    ],
  },

  httpNode: {
    nodeType: 'httpNode',
    description: 'Make HTTP request',
    category: 'communication',
    parameters: [
      {
        name: 'url',
        type: 'string',
        required: true,
        description: 'URL to request',
      },
      {
        name: 'method',
        type: 'enum',
        required: false,
        description: 'HTTP method',
        enumValues: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'],
        defaultValue: 'GET',
      },
    ],
  },

  gmail: {
    nodeType: 'gmail',
    description: 'Gmail actions',
    category: 'communication',
    parameters: [
      {
        name: 'credentialId',
        type: 'string',
        required: true,
        description: 'Gmail credential ID',
      },
      {
        name: 'operation',
        type: 'enum',
        required: true,
        description: 'Gmail operation',
        enumValues: ['send', 'get', 'getAll', 'reply'],
      },
      {
        name: 'to',
        type: 'string',
        required: false,
        description: 'Recipient email (for send operation)',
      },
      {
        name: 'subject',
        type: 'string',
        required: false,
        description: 'Email subject (for send operation)',
      },
      {
        name: 'message',
        type: 'string',
        required: false,
        description: 'Email message body (for send operation)',
      },
    ],
  },

  gmailNode: {
    nodeType: 'gmailNode',
    description: 'Gmail actions',
    category: 'communication',
    parameters: [
      {
        name: 'credentialId',
        type: 'string',
        required: true,
        description: 'Gmail credential ID',
      },
      {
        name: 'operation',
        type: 'enum',
        required: true,
        description: 'Gmail operation',
        enumValues: ['send', 'get', 'getAll', 'reply'],
      },
    ],
  },

  slack: {
    nodeType: 'slack',
    description: 'Slack messaging',
    category: 'communication',
    parameters: [
      {
        name: 'channel',
        type: 'string',
        required: true,
        description: 'Slack channel',
      },
      {
        name: 'credentialId',
        type: 'string',
        required: true,
        description: 'Slack credential ID',
      },
      {
        name: 'operation',
        type: 'enum',
        required: false,
        description: 'Slack operation',
        enumValues: ['postMessage', 'sendDirectMessage', 'updateMessage', 'deleteMessage'],
        defaultValue: 'postMessage',
      },
      {
        name: 'text',
        type: 'string',
        required: false,
        description: 'Message text',
      },
    ],
  },

  slackNode: {
    nodeType: 'slackNode',
    description: 'Send messages and interact with Slack',
    category: 'communication',
    parameters: [
      {
        name: 'channel',
        type: 'string',
        required: true,
        description: 'Slack channel',
      },
      {
        name: 'credentialId',
        type: 'string',
        required: true,
        description: 'Slack credential ID',
      },
    ],
  },

  teams: {
    nodeType: 'teams',
    description: 'Microsoft Teams messaging',
    category: 'communication',
    parameters: [
      {
        name: 'channel',
        type: 'string',
        required: true,
        description: 'Teams channel',
      },
      {
        name: 'operation',
        type: 'enum',
        required: false,
        description: 'Teams operation',
        enumValues: ['postMessage', 'updateMessage', 'getChannels'],
        defaultValue: 'postMessage',
      },
      {
        name: 'text',
        type: 'string',
        required: false,
        description: 'Message text',
      },
    ],
  },

  webhook: {
    nodeType: 'webhook',
    description: 'Webhook node',
    category: 'communication',
    parameters: [
      {
        name: 'webhookUrl',
        type: 'string',
        required: false,
        description: 'Webhook URL',
      },
    ],
  },

  webhookNode: {
    nodeType: 'webhookNode',
    description: 'Webhook node',
    category: 'communication',
    parameters: [],
  },

  // =========================================================================
  // Google Sheets Nodes
  // =========================================================================
  googleSheets: {
    nodeType: 'googleSheets',
    description: 'Google Sheets operations',
    category: 'data',
    parameters: [
      {
        name: 'credentialId',
        type: 'string',
        required: true,
        description: 'Google credential ID',
      },
      {
        name: 'spreadsheetId',
        type: 'string',
        required: true,
        description: 'Spreadsheet ID',
      },
      {
        name: 'sheetName',
        type: 'string',
        required: true,
        description: 'Sheet name',
      },
      {
        name: 'operation',
        type: 'enum',
        required: true,
        description: 'Sheet operation',
        enumValues: ['read', 'append', 'update', 'create'],
      },
    ],
  },

  googleSheetNode: {
    nodeType: 'googleSheetNode',
    description: 'Google Sheets operations',
    category: 'data',
    parameters: [
      {
        name: 'credentialId',
        type: 'string',
        required: false,
        description: 'Google credential ID',
      },
      {
        name: 'spreadsheetId',
        type: 'string',
        required: false,
        description: 'Spreadsheet ID',
      },
      {
        name: 'sheetName',
        type: 'string',
        required: false,
        description: 'Sheet name',
      },
    ],
  },

  // =========================================================================
  // AI Nodes
  // =========================================================================
  openai: {
    nodeType: 'openai',
    description: 'OpenAI GPT operations',
    category: 'ai',
    parameters: [
      {
        name: 'model',
        type: 'string',
        required: true,
        description: 'OpenAI model name',
      },
      {
        name: 'operation',
        type: 'enum',
        required: false,
        description: 'Operation type',
        enumValues: ['completion', 'chatCompletion', 'embedding'],
        defaultValue: 'chatCompletion',
      },
      {
        name: 'prompt',
        type: 'string',
        required: false,
        description: 'Prompt text',
      },
      {
        name: 'maxTokens',
        type: 'number',
        required: false,
        description: 'Maximum tokens',
      },
      {
        name: 'temperature',
        type: 'number',
        required: false,
        description: 'Temperature (0-1)',
      },
    ],
  },

  anthropic: {
    nodeType: 'anthropic',
    description: 'Anthropic Claude operations',
    category: 'ai',
    parameters: [
      {
        name: 'model',
        type: 'string',
        required: true,
        description: 'Anthropic model name',
      },
      {
        name: 'operation',
        type: 'enum',
        required: false,
        description: 'Operation type',
        enumValues: ['completion', 'chatCompletion'],
        defaultValue: 'chatCompletion',
      },
      {
        name: 'prompt',
        type: 'string',
        required: false,
        description: 'Prompt text',
      },
      {
        name: 'maxTokens',
        type: 'number',
        required: false,
        description: 'Maximum tokens',
      },
    ],
  },

  llm: {
    nodeType: 'llm',
    description: 'Generic LLM node',
    category: 'ai',
    parameters: [
      {
        name: 'provider',
        type: 'enum',
        required: false,
        description: 'LLM provider',
        enumValues: ['openai', 'anthropic', 'custom'],
      },
      {
        name: 'model',
        type: 'string',
        required: false,
        description: 'Model name',
      },
      {
        name: 'prompt',
        type: 'string',
        required: false,
        description: 'Prompt text',
      },
    ],
  },

  llmNode: {
    nodeType: 'llmNode',
    description: 'Generic LLM node',
    category: 'ai',
    parameters: [
      {
        name: 'model',
        type: 'string',
        required: false,
        description: 'Model name',
      },
    ],
  },

  generateMessage: {
    nodeType: 'generateMessage',
    description: 'Generate personalized message',
    category: 'ai',
    parameters: [
      {
        name: 'template',
        type: 'string',
        required: false,
        description: 'Message template',
      },
      {
        name: 'format',
        type: 'enum',
        required: false,
        description: 'Output format',
        enumValues: ['text', 'html', 'markdown'],
        defaultValue: 'text',
      },
    ],
  },

  hpm: {
    nodeType: 'hpm',
    description: 'Highly personalized message',
    category: 'ai',
    parameters: [
      {
        name: 'email',
        type: 'boolean',
        required: false,
        description: 'Generate email message',
      },
      {
        name: 'linkedinConnectRequest',
        type: 'boolean',
        required: false,
        description: 'Generate LinkedIn connection request',
      },
      {
        name: 'linkedinPostConnection',
        type: 'boolean',
        required: false,
        description: 'Generate LinkedIn post-connection message',
      },
    ],
  },

  hpmNode: {
    nodeType: 'hpmNode',
    description: 'Highly personalized message',
    category: 'ai',
    parameters: [
      {
        name: 'email',
        type: 'boolean',
        required: false,
        description: 'Generate email message',
      },
      {
        name: 'linkedin',
        type: 'boolean',
        required: false,
        description: 'Generate LinkedIn message',
      },
    ],
  },

  // =========================================================================
  // Platform-Specific Nodes
  // =========================================================================
  addToCampaign: {
    nodeType: 'addToCampaign',
    description: 'Add contact to campaign',
    category: 'platform',
    parameters: [
      {
        name: 'selectedCMP',
        type: 'string',
        required: true,
        description: 'Campaign ID',
      },
      {
        name: 'selectedSequence',
        type: 'string',
        required: true,
        description: 'Sequence ID (JSON string)',
      },
      {
        name: 'selectedMailBox',
        type: 'string',
        required: false,
        description: 'Mailbox ID',
      },
    ],
  },

  addToCampaignNode: {
    nodeType: 'addToCampaignNode',
    description: 'Add contact to campaign',
    category: 'platform',
    parameters: [
      {
        name: 'selectedCMP',
        type: 'string',
        required: true,
        description: 'Campaign ID',
      },
      {
        name: 'selectedSequence',
        type: 'string',
        required: true,
        description: 'Sequence ID (JSON string)',
      },
    ],
  },

  enrichContact: {
    nodeType: 'enrichContact',
    description: 'Enrich contact data',
    category: 'platform',
    parameters: [
      {
        name: 'enrichWithEmail',
        type: 'boolean',
        required: false,
        description: 'Enrich with email data',
        defaultValue: false,
      },
      {
        name: 'enrichWithPhone',
        type: 'boolean',
        required: false,
        description: 'Enrich with phone data',
        defaultValue: false,
      },
    ],
  },

  enrichContactNode: {
    nodeType: 'enrichContactNode',
    description: 'Enrich contact data',
    category: 'platform',
    parameters: [
      {
        name: 'enrichWithEmail',
        type: 'boolean',
        required: false,
        description: 'Enrich with email data',
      },
      {
        name: 'enrichWithPhone',
        type: 'boolean',
        required: false,
        description: 'Enrich with phone data',
      },
    ],
  },

  sourceContact: {
    nodeType: 'sourceContact',
    description: 'Source contacts from database',
    category: 'platform',
    parameters: [
      {
        name: 'searchCriteria',
        type: 'object',
        required: false,
        description: 'Search criteria',
      },
      {
        name: 'maxResults',
        type: 'number',
        required: false,
        description: 'Maximum results to return',
      },
    ],
  },

  sourceContactNode: {
    nodeType: 'sourceContactNode',
    description: 'Source contacts from database',
    category: 'platform',
    parameters: [],
  },

  moveToTAL: {
    nodeType: 'moveToTAL',
    description: 'Add contact to Target Account List',
    category: 'platform',
    parameters: [
      { name: 'firstName', type: 'string', required: true, description: 'Contact first name' },
      { name: 'linkedin', type: 'string', required: true, description: 'LinkedIn profile URL' },
      { name: 'domain', type: 'string', required: true, description: 'Company domain' },
      { name: 'lastName', type: 'string', required: false, description: 'Contact last name' },
      { name: 'email', type: 'string', required: false, description: 'Email address' },
      { name: 'jobTitle', type: 'string', required: false, description: 'Job title' },
      { name: 'phone', type: 'string', required: false, description: 'Phone number' },
      { name: 'department', type: 'string', required: false, description: 'Department' },
      { name: 'companyName', type: 'string', required: false, description: 'Company name' },
      { name: 'country', type: 'string', required: false, description: 'Prospect country' },
    ],
  },

  moveToTALNode: {
    nodeType: 'moveToTALNode',
    description: 'Add contact to Target Account List',
    category: 'platform',
    parameters: [
      { name: 'firstName', type: 'string', required: true, description: 'Contact first name' },
      { name: 'linkedin', type: 'string', required: true, description: 'LinkedIn profile URL' },
      { name: 'domain', type: 'string', required: true, description: 'Company domain' },
      { name: 'lastName', type: 'string', required: false, description: 'Contact last name' },
      { name: 'email', type: 'string', required: false, description: 'Email address' },
      { name: 'jobTitle', type: 'string', required: false, description: 'Job title' },
      { name: 'phone', type: 'string', required: false, description: 'Phone number' },
      { name: 'department', type: 'string', required: false, description: 'Department' },
      { name: 'companyName', type: 'string', required: false, description: 'Company name' },
      { name: 'country', type: 'string', required: false, description: 'Prospect country' },
    ],
  },

  addContactToTal: {
    nodeType: 'addContactToTal',
    description: 'Add contact to Target Account List',
    category: 'platform',
    parameters: [],
  },

  moveToAccountTAL: {
    nodeType: 'moveToAccountTAL',
    description: 'Add account to Target Account List',
    category: 'platform',
    parameters: [
      {
        name: 'domain',
        type: 'string',
        required: true,
        description: 'Company domain',
      },
    ],
  },

  moveToAccountTALNode: {
    nodeType: 'moveToAccountTALNode',
    description: 'Add account to Target Account List',
    category: 'platform',
    parameters: [
      {
        name: 'companyDomain',
        type: 'string',
        required: true,
        description: 'Company domain',
        // Domain is normally supplied via a dynamicFields mapping from an
        // upstream node's output (e.g. account.Account Domain), not a flat string.
        dynamicFieldEntityNames: ['Domain', 'domain'],
      },
    ],
  },

  addAccountToTal: {
    nodeType: 'addAccountToTal',
    description: 'Add account to Target Account List',
    category: 'platform',
    parameters: [],
  },

  assignContact: {
    nodeType: 'assignContact',
    description: 'Assign contact to owner',
    category: 'platform',
    parameters: [
      {
        name: 'assignTo',
        type: 'object',
        required: false,
        description: 'Assignment configuration',
      },
    ],
  },

  assignContactNode: {
    nodeType: 'assignContactNode',
    description: 'Assign contact to owner',
    category: 'platform',
    parameters: [],
  },

  criteriaContact: {
    nodeType: 'criteriaContact',
    description: 'Filter contacts by criteria',
    category: 'platform',
    parameters: [
      {
        name: 'criteria',
        type: 'object',
        required: false,
        description: 'Filter criteria',
      },
    ],
  },

  criteriaContactNode: {
    nodeType: 'criteriaContactNode',
    description: 'Filter contacts by criteria',
    category: 'platform',
    parameters: [],
  },

  criteriaAccount: {
    nodeType: 'criteriaAccount',
    description: 'Filter accounts by criteria',
    category: 'platform',
    parameters: [
      {
        name: 'criteria',
        type: 'object',
        required: false,
        description: 'Filter criteria',
      },
    ],
  },

  criteriaAccountNode: {
    nodeType: 'criteriaAccountNode',
    description: 'Filter accounts by criteria',
    category: 'platform',
    parameters: [],
  },

  linkedinSearch: {
    nodeType: 'linkedinSearch',
    description: 'Search LinkedIn',
    category: 'platform',
    parameters: [
      {
        name: 'searchQuery',
        type: 'string',
        required: false,
        description: 'Search query',
      },
    ],
  },

  linkedinSearchNode: {
    nodeType: 'linkedinSearchNode',
    description: 'Search LinkedIn',
    category: 'platform',
    parameters: [],
  },

  enrichDomain: {
    nodeType: 'enrichDomain',
    description: 'Enrich domain/company data',
    category: 'platform',
    parameters: [
      {
        name: 'domain',
        type: 'string',
        required: false,
        description: 'Domain to enrich',
      },
    ],
  },

  enrichDomainNode: {
    nodeType: 'enrichDomainNode',
    description: 'Person LinkedIn Lookup',
    category: 'platform',
    parameters: [
      {
        name: 'companyDomain',
        type: 'string',
        required: true,
        description: 'Company domain',
      },
      {
        name: 'firstName',
        type: 'string',
        required: true,
        description: 'Person first name',
      },
    ],
  },

  personLinkedinLookup: {
    nodeType: 'personLinkedinLookup',
    description: 'Person LinkedIn Lookup',
    category: 'platform',
    parameters: [
      {
        name: 'companyDomain',
        type: 'string',
        required: false,
        description: 'Company domain',
      },
      {
        name: 'firstName',
        type: 'string',
        required: false,
        description: 'Person first name',
      },
    ],
  },

  addToListNode: {
    nodeType: 'addToListNode',
    description: 'Add a contact to a named customer list',
    category: 'platform',
    parameters: [
      {
        name: 'listName',
        type: 'string',
        required: true,
        description: 'Name of the list to add the contact to',
      },
    ],
  },

  addAccountsByICP: {
    nodeType: 'addAccountsByICP',
    description: 'Add accounts to TAL using an ICP filter',
    category: 'platform',
    parameters: [
      {
        name: 'icpPrimaryKey',
        type: 'string',
        required: true,
        description: 'ICP filter to use for discovering accounts',
      },
    ],
  },

  databaseSearchNode: {
    nodeType: 'databaseSearchNode',
    description: 'Search MDQS database for account or contact records',
    category: 'platform',
    parameters: [
      {
        name: 'entityType',
        type: 'enum',
        required: true,
        description: 'Type of record to search for',
        enumValues: ['account', 'contact'],
      },
    ],
  },
};

/**
 * Get parameter schema for a node type
 */
export function getNodeParameterSchema(nodeType: string): NodeParameterSchema | undefined {
  return NODE_PARAMETER_SCHEMAS[nodeType];
}

/**
 * Get required parameters for a node type
 */
export function getRequiredParameters(nodeType: string): ParameterDefinition[] {
  const schema = NODE_PARAMETER_SCHEMAS[nodeType];
  if (!schema) return [];
  return schema.parameters.filter((p) => p.required);
}

/**
 * Check if a node type is known/supported
 */
export function isKnownNodeType(nodeType: string): boolean {
  return nodeType in NODE_PARAMETER_SCHEMAS;
}
